import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import clsx from "clsx";
import React from "react";
import {ICategory} from "@evennt-tech/api-v2-core";
import {useGlossarySearchQuery} from "store/glossary/glossary.store.selectors";
import {ISearchedCategory} from "hooks/show/glossary/useGlossarySearch";
import useStyles from "./styles";

export type Filter = {
  title: string;
  id: string;
  /**
   * Rendered after the title — Show Info uses it for the book icon that marks a
   * category as inherited from the library.
   */
  adornment?: React.ReactNode;
} & Pick<ISearchedCategory, "matchesCount">;

export type FilterMenuProps<T extends Filter> = {
  filters: T[];
  onSelect: (item: T | undefined) => void;
  allCategory: T;
  /**
   * Receives the click event so a caller can anchor a menu to the row — the Show
   * Info rail opens the same `Add from Library` / `Add local` menu the page's `+`
   * does. Callers that only need to open a modal can ignore the argument.
   */
  onClickAdd?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  addLabel?: string;
  includeAll?: boolean;
  selectedCategory?: ICategory | null;
};

const FilterMenu = <T extends Filter>({
  filters: baseFilters,
  selectedCategory,
  onSelect,
  includeAll,
  addLabel,
  onClickAdd,
  allCategory,
}: FilterMenuProps<T>) => {
  const classes = useStyles();

  const searchQuery = useGlossarySearchQuery();
  const filters: T[] = includeAll ? [allCategory, ...baseFilters] : baseFilters;

  const onClickItem = (filter: T) => {
    onSelect(filter.title === allCategory.title ? allCategory : filter);
  };

  const isSelected = (filter: T) => {
    if (!selectedCategory && filter === allCategory) {
      return true;
    }
    return selectedCategory?.id === filter.id;
  };

  const getTitle = (filter: T) => {
    if (!searchQuery) {
      return filter.title;
    }

    if (!filter.matchesCount) {
      return filter.title;
    }

    return `${filter.title} (${filter.matchesCount})`;
  };

  const renderFilters = () =>
    filters.map((filter: T) => {
      const disabled = isSelected(filter);

      return (
        <ListItem
          key={filter.id}
          disabled={disabled}
          button
          classes={{
            root: classes.listItem,
            disabled: classes.listItemSelected,
          }}
          onClick={() => {
            onClickItem(filter);
          }}
        >
          <ListItemText
            classes={{primary: classes.listItemText}}
            primary={
              filter.adornment ? (
                <>
                  {getTitle(filter)} {filter.adornment}
                </>
              ) : (
                getTitle(filter)
              )
            }
          />
        </ListItem>
      );
    });

  return (
    <List className={classes.root}>
      {renderFilters()}
      {onClickAdd && addLabel && (
        <ListItem
          className={clsx(classes.listItem, classes.addButton)}
          button
          onClick={onClickAdd}
        >
          <ListItemText
            classes={{primary: classes.listItemText}}
            primary={addLabel}
          />
        </ListItem>
      )}
    </List>
  );
};

export default FilterMenu;
